import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { DICTATION_ACK_KEY } from '../lib/dictation';

const ShiftContext = createContext();

const STORAGE_KEY = 'shiftready:shift';
const LANG_KEY = 'shiftready:lang';
const PIN_KEY = 'shiftready:pin';
const BIO_KEY = 'shiftready:bio';
const INITIALS_KEY = 'shiftready:initials';
const IDLE_MIN_KEY = 'shiftready:idle-min';
const FORCE_ROTATE_KEY = 'shiftready:force-pin-rotate';
const ACCESS_LOG_KEY = 'shiftready:access-log';
const CUSTOM_PHRASES_KEY = 'shiftready:custom-phrases';
const NAMESPACE = 'shiftready:';
const GRACE_MS = 2 * 60 * 60 * 1000;
const SOFT_ROTATE_MS = 12 * 60 * 60 * 1000;
const MAX_LOG_EVENTS = 30;
const DEFAULT_IDLE_MIN = 5;
const MAX_PIN_ATTEMPTS = 5;

// Keys that survive shift wipes — these are user preferences / personal
// libraries with NO PHI. Custom phrases persist across shifts so the nurse
// builds a personal library over time (with a UI warning to never include PHI).
const WIPE_EXEMPT = new Set([LANG_KEY, CUSTOM_PHRASES_KEY, DICTATION_ACK_KEY]);

function wipeAll() {
  const toRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith(NAMESPACE) && !WIPE_EXEMPT.has(k)) toRemove.push(k);
  }
  toRemove.forEach((k) => localStorage.removeItem(k));
}

function bootWipeIfStale() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const shift = JSON.parse(raw);
    // Suspended shift: only wipe if user missed the next-start by >24 hr
    if (shift.suspended && shift.nextStart) {
      if (Date.now() - shift.nextStart > 24 * 3600 * 1000) {
        wipeAll();
        return null;
      }
      return shift;
    }
    // Final shift of a block (or single shift) — wipe if past end + grace.
    const isFinal = !shift.blockTotal || (shift.blockCurrent || 1) >= shift.blockTotal;
    if (isFinal && Date.now() > shift.end + GRACE_MS) {
      wipeAll();
      return null;
    }
    return shift;
  } catch {
    return null;
  }
}

function readPin() {
  try {
    const raw = localStorage.getItem(PIN_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function readBio() {
  try {
    const raw = localStorage.getItem(BIO_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function ShiftProvider({ children }) {
  const [shift, setShift] = useState(() => bootWipeIfStale());
  const [pin, setPin] = useState(() => readPin());
  const [bio, setBio] = useState(() => readBio());
  // Lock on boot if shift active AND (pin OR bio) set
  const [locked, setLocked] = useState(() => {
    const s = bootWipeIfStale();
    return Boolean(s && (readPin() || readBio()));
  });
  const [lang, setLang] = useState(() => localStorage.getItem(LANG_KEY) || 'en');
  const [nurseInitials, setNurseInitialsState] = useState(() => localStorage.getItem(INITIALS_KEY) || '');
  const [idleTimeoutMin, setIdleTimeoutMinState] = useState(() => {
    const v = parseInt(localStorage.getItem(IDLE_MIN_KEY) || '', 10);
    return Number.isFinite(v) && v > 0 ? v : DEFAULT_IDLE_MIN;
  });
  const [forcePinRotate, setForcePinRotateState] = useState(() => localStorage.getItem(FORCE_ROTATE_KEY) === '1');
  const [accessLog, setAccessLog] = useState(() => {
    try {
      const raw = localStorage.getItem(ACCESS_LOG_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [customPhrases, setCustomPhrases] = useState(() => {
    try {
      const raw = localStorage.getItem(CUSTOM_PHRASES_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  // Transient (not persisted): PIN-rotation prompt state after resume.
  // { needed: bool, forced: bool, snoozed: bool }
  const [pinRotate, setPinRotate] = useState({ needed: false, forced: false, snoozed: false });
  const [appHidden, setAppHidden] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [complete, setComplete] = useState(false);
  const [idleWarn, setIdleWarn] = useState(false);
  const lastActivityRef = useRef(Date.now());

  const IDLE_MS = idleTimeoutMin * 60 * 1000;
  const IDLE_WARN_MS = Math.max(10000, IDLE_MS - 60 * 1000);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (shift) localStorage.setItem(STORAGE_KEY, JSON.stringify(shift));
    else localStorage.removeItem(STORAGE_KEY);
  }, [shift]);

  useEffect(() => {
    if (pin) localStorage.setItem(PIN_KEY, JSON.stringify(pin));
    else localStorage.removeItem(PIN_KEY);
  }, [pin]);

  useEffect(() => {
    if (bio) localStorage.setItem(BIO_KEY, JSON.stringify(bio));
    else localStorage.removeItem(BIO_KEY);
  }, [bio]);

  useEffect(() => {
    localStorage.setItem(LANG_KEY, lang);
  }, [lang]);

  useEffect(() => {
    if (nurseInitials) localStorage.setItem(INITIALS_KEY, nurseInitials);
    else localStorage.removeItem(INITIALS_KEY);
  }, [nurseInitials]);

  useEffect(() => {
    localStorage.setItem(IDLE_MIN_KEY, String(idleTimeoutMin));
  }, [idleTimeoutMin]);

  useEffect(() => {
    localStorage.setItem(FORCE_ROTATE_KEY, forcePinRotate ? '1' : '0');
  }, [forcePinRotate]);

  useEffect(() => {
    try { localStorage.setItem(ACCESS_LOG_KEY, JSON.stringify(accessLog)); } catch (_) { /* quota */ }
  }, [accessLog]);

  useEffect(() => {
    try { localStorage.setItem(CUSTOM_PHRASES_KEY, JSON.stringify(customPhrases)); } catch (_) { /* quota */ }
  }, [customPhrases]);

  const addCustomPhrase = useCallback((phrase) => {
    // phrase: { label, templates: { en, [langCode]: string } }
    const clean = {
      id: 'cp-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
      label: (phrase.label || '').slice(0, 60),
      createdAt: Date.now(),
      templates: phrase.templates || {},
    };
    if (!clean.templates.en) return null;
    setCustomPhrases((prev) => [clean, ...prev].slice(0, 200));
    return clean.id;
  }, []);

  const updateCustomPhrase = useCallback((id, patch) => {
    setCustomPhrases((prev) => prev.map((p) => p.id === id
      ? { ...p, ...patch, templates: { ...p.templates, ...(patch.templates || {}) } }
      : p));
  }, []);

  const removeCustomPhrase = useCallback((id) => {
    setCustomPhrases((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const clearCustomPhrases = useCallback(() => setCustomPhrases([]), []);

  const setForcePinRotate = useCallback((v) => setForcePinRotateState(Boolean(v)), []);

  const appendAccessLog = useCallback((type) => {
    setAccessLog((prev) => {
      const next = [{ ts: Date.now(), type }, ...prev].slice(0, MAX_LOG_EVENTS);
      return next;
    });
  }, []);

  const clearAccessLog = useCallback(() => setAccessLog([]), []);

  const setNurseInitials = useCallback((v) => {
    const clean = (v || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4);
    setNurseInitialsState(clean);
  }, []);

  const setIdleTimeoutMin = useCallback((v) => {
    const n = parseInt(v, 10);
    if (Number.isFinite(n) && n > 0 && n <= 120) setIdleTimeoutMinState(n);
  }, []);

  useEffect(() => {
    const bump = () => {
      lastActivityRef.current = Date.now();
      setIdleWarn(false);
    };
    const events = ['pointerdown', 'keydown', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, bump));
  }, []);

  // Auto-lock on tab hide if PIN set; always set appHidden for screenshot deterrent blur
  useEffect(() => {
    const onVis = () => {
      const hidden = document.visibilityState === 'hidden';
      setAppHidden(hidden);
      if (hidden && shift && (pin || bio)) {
        setLocked(true);
      }
    };
    const onBlur = () => setAppHidden(true);
    const onFocus = () => setAppHidden(false);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
    };
  }, [shift, pin, bio]);

  // Watchdog — re-binds when idle timeout changes so user setting takes effect immediately
  useEffect(() => {
    if (!shift) return;
    const id = setInterval(() => {
      const idle = Date.now() - lastActivityRef.current;
      // Multi-shift block: while suspended, only wipe if user missed the next start by >24 hr
      if (shift.suspended) {
        const missedBy = shift.nextStart ? Date.now() - shift.nextStart : 0;
        if (missedBy > 24 * 3600 * 1000) {
          wipeAll();
          setShift(null);
          setPin(null);
          setComplete(true);
          setIdleWarn(false);
          setLocked(false);
        }
        return; // no idle-wipe / no expiry check while suspended
      }
      // Final shift of a block? Wipe at end + grace, same as single shift.
      const isFinal = !shift.blockTotal || (shift.blockCurrent || 1) >= shift.blockTotal;
      const expired = isFinal && Date.now() > shift.end + GRACE_MS;
      if (expired) {
        wipeAll();
        setShift(null);
        setPin(null);
        setComplete(true);
        setIdleWarn(false);
        setLocked(false);
        return;
      }
      if (idle >= IDLE_MS) {
        if (pin || bio) {
          setLocked(true);
          setIdleWarn(false);
        } else {
          wipeAll();
          setShift(null);
          setComplete(true);
          setIdleWarn(false);
        }
      } else if (idle >= IDLE_WARN_MS && !pin && !bio) {
        setIdleWarn(true);
      } else {
        setIdleWarn(false);
      }
    }, 10000);
    return () => clearInterval(id);
  }, [shift, pin, bio, IDLE_MS, IDLE_WARN_MS]);

  const startShift = useCallback((durationHours, startTimestamp, pinRecord, bioRecord, blockTotal) => {
    const start = startTimestamp || Date.now();
    const end = start + durationHours * 3600 * 1000;
    setShift({
      start, end, durationHours, extended: 0,
      blockTotal: blockTotal || null,
      blockCurrent: blockTotal ? 1 : null,
      suspended: false,
      nextStart: null,
    });
    setComplete(false);
    lastActivityRef.current = Date.now();
    setPin(pinRecord ? { ...pinRecord, attempts: 0 } : null);
    setBio(bioRecord || null);
    setLocked(false);
  }, []);

  const suspendShift = useCallback((nextStartTs) => {
    // Enter suspended state — keeps patient roster, locks the app, pauses timer.
    setShift((s) => (s ? { ...s, suspended: true, nextStart: nextStartTs, suspendedAt: Date.now() } : s));
    setLocked(true);
    setIdleWarn(false);
    appendAccessLog('shift-suspend');
  }, [appendAccessLog]);

  const resumeShift = useCallback(() => {
    // Called after successful PIN/bio unlock while suspended.
    // Starts the next shift in the block with a fresh timer; preserves data.
    setShift((s) => {
      if (!s || !s.suspended) return s;
      const start = Date.now();
      const end = start + (s.durationHours || 12) * 3600 * 1000;
      return {
        ...s,
        start, end, extended: 0,
        blockCurrent: (s.blockCurrent || 1) + 1,
        suspended: false,
        nextStart: null,
      };
    });
    lastActivityRef.current = Date.now();
  }, []);

  const extendShift = useCallback((minutes) => {
    setShift((s) => (s ? { ...s, end: s.end + minutes * 60 * 1000, extended: (s.extended || 0) + minutes } : s));
    lastActivityRef.current = Date.now();
  }, []);

  const endShift = useCallback(() => {
    wipeAll();
    setShift(null);
    setPin(null);
    setBio(null);
    setComplete(true);
    setIdleWarn(false);
    setLocked(false);
  }, []);

  const lockNow = useCallback(() => {
    if (pin || bio) setLocked(true);
  }, [pin, bio]);

  const tryUnlock = useCallback(async (entered) => {
    if (!pin) { setLocked(false); return { ok: true, attemptsLeft: MAX_PIN_ATTEMPTS }; }
    const { verifyPin } = await import('../hooks/usePin');
    const ok = await verifyPin(entered, pin.salt, pin.hash);
    if (ok) {
      setPin({ ...pin, attempts: 0 });
      setLocked(false);
      lastActivityRef.current = Date.now();
      // Check if we're resuming from a suspended state; capture suspend gap BEFORE mutating shift.
      const wasSuspended = Boolean(shift?.suspended);
      const suspendGap = wasSuspended && shift.suspendedAt ? Date.now() - shift.suspendedAt : 0;
      if (wasSuspended) {
        resumeShift();
        appendAccessLog('shift-resume');
        // Trigger PIN-rotation prompt:
        //  - Forced if user opted in via Privacy Settings
        //  - Soft if suspend gap > 12 hr (defer until user completes resume)
        if (forcePinRotate) {
          setPinRotate({ needed: true, forced: true, snoozed: false });
        } else if (suspendGap >= SOFT_ROTATE_MS) {
          setPinRotate({ needed: true, forced: false, snoozed: false });
        }
      } else {
        appendAccessLog('unlock-pin');
      }
      return { ok: true, attemptsLeft: MAX_PIN_ATTEMPTS };
    }
    const attempts = (pin.attempts || 0) + 1;
    if (attempts >= MAX_PIN_ATTEMPTS) {
      wipeAll();
      setShift(null);
      setPin(null);
      setLocked(false);
      setComplete(true);
      return { ok: false, attemptsLeft: 0, wiped: true };
    }
    setPin({ ...pin, attempts });
    appendAccessLog('unlock-failed');
    return { ok: false, attemptsLeft: MAX_PIN_ATTEMPTS - attempts };
  }, [pin, shift, resumeShift, forcePinRotate, appendAccessLog]);

  const tryUnlockBio = useCallback(async () => {
    if (!bio?.credentialId) return { ok: false };
    const { verifyBiometric } = await import('../hooks/useBiometric');
    const ok = await verifyBiometric(bio.credentialId);
    if (ok) {
      setLocked(false);
      lastActivityRef.current = Date.now();
      if (pin) setPin({ ...pin, attempts: 0 });
      const wasSuspended = Boolean(shift?.suspended);
      const suspendGap = wasSuspended && shift.suspendedAt ? Date.now() - shift.suspendedAt : 0;
      if (wasSuspended) {
        resumeShift();
        appendAccessLog('shift-resume-bio');
        if (forcePinRotate) {
          setPinRotate({ needed: true, forced: true, snoozed: false });
        } else if (suspendGap >= SOFT_ROTATE_MS) {
          setPinRotate({ needed: true, forced: false, snoozed: false });
        }
      } else {
        appendAccessLog('unlock-bio');
      }
    }
    return { ok };
  }, [bio, pin, shift, resumeShift, forcePinRotate, appendAccessLog]);

  const dismissIdle = useCallback(() => {
    lastActivityRef.current = Date.now();
    setIdleWarn(false);
  }, []);

  const changePin = useCallback(async (oldPin, newPin) => {
    if (!pin) return { ok: false, error: 'No PIN set' };
    const { hashPin, verifyPin } = await import('../hooks/usePin');
    const okOld = await verifyPin(oldPin, pin.salt, pin.hash);
    if (!okOld) return { ok: false, error: 'Current PIN is incorrect.' };
    if (!newPin || newPin.length < 4) return { ok: false, error: 'New PIN must be 4+ digits.' };
    if (newPin === oldPin) return { ok: false, error: 'New PIN must differ from current PIN.' };
    const fresh = await hashPin(newPin);
    setPin({ hash: fresh.hash, salt: fresh.salt, attempts: 0 });
    setPinRotate({ needed: false, forced: false, snoozed: false });
    appendAccessLog('pin-changed');
    return { ok: true };
  }, [pin, appendAccessLog]);

  const dismissPinRotate = useCallback(() => {
    setPinRotate({ needed: false, forced: false, snoozed: false });
  }, []);

  const snoozePinRotate = useCallback(() => {
    // "Don't ask this block" — silence the soft prompt for the remainder of this block.
    setPinRotate({ needed: false, forced: false, snoozed: true });
    appendAccessLog('pin-rotate-snoozed');
  }, [appendAccessLog]);

  const resetComplete = useCallback(() => setComplete(false), []);

  const remainingMs = shift ? Math.max(0, shift.end - now) : 0;
  const elapsedMs = shift ? Math.max(0, now - shift.start) : 0;
  const totalMs = shift ? shift.end - shift.start : 0;

  return (
    <ShiftContext.Provider
      value={{
        shift, now, remainingMs, elapsedMs, totalMs,
        startShift, extendShift, endShift,
        suspendShift, resumeShift,
        complete, resetComplete,
        lang, setLang,
        idleWarn, dismissIdle,
        pin: pin ? { set: true, attempts: pin.attempts || 0 } : { set: false, attempts: 0 },
        bio: { set: Boolean(bio) },
        locked, lockNow, tryUnlock, tryUnlockBio,
        nurseInitials, setNurseInitials,
        idleTimeoutMin, setIdleTimeoutMin,
        forcePinRotate, setForcePinRotate,
        pinRotate, dismissPinRotate, snoozePinRotate, changePin,
        accessLog, clearAccessLog,
        customPhrases, addCustomPhrase, updateCustomPhrase, removeCustomPhrase, clearCustomPhrases,
        appHidden,
      }}
    >
      {children}
    </ShiftContext.Provider>
  );
}

export function useShift() {
  const ctx = useContext(ShiftContext);
  if (!ctx) throw new Error('useShift outside provider');
  return ctx;
}

export function useSessionState(key, initial) {
  const fullKey = `${NAMESPACE}${key}`;
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(fullKey);
      return raw ? JSON.parse(raw) : initial;
    } catch { return initial; }
  });
  useEffect(() => {
    try { localStorage.setItem(fullKey, JSON.stringify(state)); }
    catch (e) { console.warn('shiftready: localStorage write failed', fullKey, e); }
  }, [fullKey, state]);
  return [state, setState];
}

export function formatHMS(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}
